import { Routes, Route } from "react-router-dom";
import { WorkshopLayout } from "./workshop-layout";
import { WorkshopIndex } from "./workshop-index";
import { ExportIsolatedPage } from "./pages/export/export-isolated-page";
import { LoginPage } from "./pages/auth/login-page";
import { RegisterPage } from "./pages/auth/register-page";
import { RegisterSuccessPage } from "./pages/auth/register-success-page";
import { ForgotPasswordPage } from "./pages/auth/forgot-password-page";
import { ResetPasswordPage } from "./pages/auth/reset-password-page";
import { VerifyEmailPage } from "./pages/auth/verify-email-page";
import { AuthErrorPage } from "./pages/auth/auth-error-page";
import { SettingsPage } from "./pages/settings-page";
import { FullAppPage } from "./pages/full-app-page";
import { ShapesPage } from "./pages/shapes/shapes-page";
import { WorkflowPage } from "./pages/shapes/workflow-page";
import { MemoryPage } from "./pages/shapes/memory-page";
import { TravelFramesPage } from "./pages/shapes/travel-frames-page";
import { MessagingFramesPage } from "./pages/shapes/messaging-frames-page";
import { TaskMusicLinkFramesPage } from "./pages/shapes/task-music-link-frames-page";
import { DataFramesPage } from "./pages/shapes/data-frames-page";
import { EventAgendaFramesPage } from "./pages/shapes/event-agenda-frames-page";
import { PreviewBoardFramesPage } from "./pages/shapes/preview-board-frames-page";
import { ConversationEmbedsPage } from "./pages/shapes/conversation-embeds-page";
import { WidgetGalleryPage } from "./pages/shapes/widget-gallery-page";
import { StageRailPage } from "./pages/cursors/stage-rail-page";
import { ComposerRedesignPage } from "./pages/panels/composer-redesign-page";
import { PanelsPage } from "./pages/panels/panels-page";
import { FramePanelsPage } from "./pages/panels/frame-panels-page";
import { CanvasPage } from "./pages/canvas/canvas-page";
import { LiquidGlassRailPage } from "./pages/canvas/liquid-glass-rail-page";
import { DenkerPresenceSwitcherPage } from "./pages/canvas/denker-presence-switcher-page";
import { ErrorHandlingPage } from "./pages/canvas/error-handling-page";
import { FeedbackPage } from "./pages/canvas/feedback-page";
import { DenkerFirstOpenPage } from "./pages/journeys/denker-first-open-page";
import { CanvasSessionPage }  from "./pages/journeys/canvas-session-page";
import { ControlsTrustPage }  from "./pages/journeys/controls-trust-page";
import { ExtendAutomatePage } from "./pages/journeys/extend-automate-page";
import { MemoryHistoryPage }  from "./pages/journeys/memory-history-page";
import { GlassLabPage } from "./pages/glass-lab-page";
import { SyncStatusPage } from "./pages/sync-status-page";
import { ComponentPreviewPage } from "./pages/component-preview-page";
import { ProjectManagementPage } from "./pages/project-management-page";
import { CompactAgentDockPage } from "./pages/cursors/compact-agent-dock-page";
import { FloatingToastCardsPage } from "./pages/cursors/floating-toast-cards-page";
import { VoiceIndicatorPage } from "./pages/cursors/voice-indicator-page";
import { RecipesPage } from "./pages/design-system/recipes-page";
import { ExportPage } from "./pages/export/export-page";

/**
 * Workshop route registry.
 *
 * To add a new workshop page:
 * 1. Create your page component in the matching workshop directory
 * 2. Import it here
 * 3. Add an entry to WORKSHOP_PAGES
 * 4. The page will appear in the sidebar nav and be routable at /workshop/<path>
 */

export interface WorkshopPage {
  path: string;
  label: string;
  category: "pages" | "shapes" | "panels" | "canvas" | "cursors" | "layout" | "journeys";
  element: React.ReactNode;
}

// --- Register workshop pages here ---
export const WORKSHOP_PAGES: WorkshopPage[] = [
  { path: "auth/login", label: "Login", category: "pages", element: <LoginPage /> },
  { path: "auth/register", label: "Register", category: "pages", element: <RegisterPage /> },
  { path: "auth/register-success", label: "Register Success", category: "pages", element: <RegisterSuccessPage /> },
  { path: "auth/forgot-password", label: "Forgot Password", category: "pages", element: <ForgotPasswordPage /> },
  { path: "auth/reset-password", label: "Reset Password", category: "pages", element: <ResetPasswordPage /> },
  { path: "auth/verify-email", label: "Verify Email (Success)", category: "pages", element: <VerifyEmailPage initialStatus="success" /> },
  { path: "auth/verify-email-loading", label: "Verify Email (Loading)", category: "pages", element: <VerifyEmailPage initialStatus="verifying" /> },
  { path: "auth/verify-email-error", label: "Verify Email (Error)", category: "pages", element: <VerifyEmailPage initialStatus="error" /> },
  { path: "auth/error", label: "Auth Error", category: "pages", element: <AuthErrorPage /> },
  { path: "settings", label: "Settings", category: "pages", element: <SettingsPage /> },
  { path: "full-app", label: "Full App Preview", category: "layout", element: <FullAppPage /> },
  { path: "shapes", label: "Frame Shapes", category: "shapes", element: <ShapesPage /> },
  { path: "shapes/workflow", label: "Workflow Frame", category: "shapes", element: <WorkflowPage /> },
  { path: "shapes/memory", label: "Memory Frame", category: "shapes", element: <MemoryPage /> },
  { path: "shapes/travel", label: "Travel Frames", category: "shapes", element: <TravelFramesPage /> },
  { path: "shapes/messaging", label: "Messaging Frames", category: "shapes", element: <MessagingFramesPage /> },
  { path: "shapes/task-music-link", label: "Task Music Link Frames", category: "shapes", element: <TaskMusicLinkFramesPage /> },
  { path: "shapes/data-frames", label: "Data Frames", category: "shapes", element: <DataFramesPage /> },
  { path: "shapes/event-agenda", label: "Event Agenda Frames", category: "shapes", element: <EventAgendaFramesPage /> },
  { path: "shapes/preview-board", label: "Preview Board Frames", category: "shapes", element: <PreviewBoardFramesPage /> },
  { path: "shapes/conversation-embeds", label: "Conversation Embeds", category: "shapes", element: <ConversationEmbedsPage /> },
  { path: "shapes/widget-gallery", label: "Widget Gallery", category: "shapes", element: <WidgetGalleryPage /> },
  { path: "cursors/stage-rail", label: "Stage Rail", category: "cursors", element: <StageRailPage /> },
  { path: "panels/composer-redesign", label: "Composer Redesign", category: "panels", element: <ComposerRedesignPage /> },
  { path: "panels", label: "Panel Components", category: "panels", element: <PanelsPage /> },
  { path: "panels/frame-panels", label: "Frame Right Panels", category: "panels", element: <FramePanelsPage /> },
  { path: "canvas", label: "Canvas & Cursors", category: "canvas", element: <CanvasPage /> },
  { path: "canvas/liquid-glass-rail", label: "Liquid Glass Rail", category: "canvas", element: <LiquidGlassRailPage /> },
  { path: "canvas/denker-presence-switcher", label: "Denker Presence Switcher", category: "canvas", element: <DenkerPresenceSwitcherPage /> },
  { path: "cursors/compact-agent-dock", label: "Compact Agent Dock", category: "cursors", element: <CompactAgentDockPage /> },
  { path: "cursors/floating-toast-cards", label: "Floating Toast Cards", category: "cursors", element: <FloatingToastCardsPage /> },
  { path: "cursors/voice-indicator", label: "Voice Indicator", category: "cursors", element: <VoiceIndicatorPage /> },
  { path: "canvas/error-handling", label: "Error Handling", category: "canvas", element: <ErrorHandlingPage /> },
  { path: "canvas/feedback", label: "Feedback Button", category: "canvas", element: <FeedbackPage /> },
  { path: "journeys/denker-first-open", label: "Denker First Open", category: "journeys", element: <DenkerFirstOpenPage /> },
  { path: "journeys/canvas-session",  label: "Canvas Work Session", category: "journeys", element: <CanvasSessionPage /> },
  { path: "journeys/controls-trust",  label: "Controls & Trust",    category: "journeys", element: <ControlsTrustPage /> },
  { path: "journeys/extend-automate", label: "Extend & Automate",   category: "journeys", element: <ExtendAutomatePage /> },
  { path: "journeys/memory-history",  label: "Memory & History",    category: "journeys", element: <MemoryHistoryPage /> },
  { path: "glass-lab", label: "Glass Lab", category: "layout", element: <GlassLabPage /> },
  { path: "sync-status", label: "Sync Status", category: "layout", element: <SyncStatusPage /> },
  { path: "preview", label: "Component Preview", category: "layout", element: <ComponentPreviewPage /> },
  { path: "project-management", label: "Project Management", category: "panels", element: <ProjectManagementPage /> },
  { path: "design-system/recipes", label: "Recipe Showcase", category: "pages", element: <RecipesPage /> },
  { path: "export", label: "Figma Export", category: "layout", element: <ExportPage /> },
];

export function WorkshopRoutes() {
  return (
    <Routes>
      {/* Isolated component view — no WorkshopLayout chrome */}
      <Route path="export/:componentId" element={<ExportIsolatedPage />} />
      <Route element={<WorkshopLayout />}>
        <Route index element={<WorkshopIndex />} />
        {WORKSHOP_PAGES.map((page) => (
          <Route key={page.path} path={page.path} element={page.element} />
        ))}
      </Route>
    </Routes>
  );
}
